import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataTableNoFilter } from "@/components/data-table-no-filter";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressao } from "@/components/ImpressaoComponent";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useItem } from "@/contexts/ItemContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { useSocket } from "@/contexts/SocketContext";
import { alert } from "@/contexts/AlertModalContext";

import inovaone_api from "@/services/inovaone_api";
import type { ImpressaoItem } from "@/types/impressao-item";
import type { ImpressaoItemResponse } from "@/types/Impressao-item-response";
import {
  brDateToISO,
  converterListaZplItemParaUrl,
  formatarData,
} from "@/utils/UtilFuncoes";

import type { ColumnDef } from "@tanstack/react-table";
import {
  ArrowLeft,
  ExternalLink,
  Eye,
  HelpCircle,
  Inbox,
  MapPin,
  Package,
  Printer,
  RefreshCw,
  Scan,
  Search,
  Tag,
  X,
} from "lucide-react";

function ReimprimirEtiqueta() {
  const { t } = useTranslation();
  const { adicionarItensNaFila } = useImpressao();
  const { isLoading, setIsLoading } = useGlobalLoader();
  const { coletaZplPadrao, impressaoConfig } = useImpressaoConfig();
  const { contaConfig } = useContaConfig();
  const { pesquisarItensNaBase } = useItem();
  const { coletaLocalizacoes, localizacoes } = useLocalizacoes();
  const { coletaCategorias, categorias } = useCategorias();
  const { coletaSeriais } = useSeriais();
  const { lastMessage, enviarMensagem } = useSocket();

  const [itemSelecionado, setItemSelecionado] = useState<
    ImpressaoItemResponse | undefined
  >(undefined);
  const [epc, setEpc] = useState<string>("");
  const [itens, setItens] = useState<ImpressaoItemResponse[]>([]);
  const [pagina, setPagina] = useState<number>(0);
  const [totalPaginas, setTotalPaginas] = useState<number>(0);

  // ---- Fetch ----
  const pesquisarItens = async (filtro: string = "*") => {
    setIsLoading(true);
    try {
      const response = await inovaone_api.get<ImpressaoItemResponse[]>(
        `/itensPage/${impressaoConfig?.id_conta}/*/*/${filtro}/*/*/*/*/*/*/*/*/*/*/*/${pagina * 10}?limit=10`,
      );
      const data = response.data;
      const response_filter: any = data.pop();
      const total_reg = response_filter?.total_reg || 0;
      const total_pages = Math.ceil(total_reg / 10) || 0;
      setTotalPaginas(total_pages);
      setItens(data);
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const lerEtiqueta = () => enviarMensagem("LEPR");

  const coletarItem = async (tag: string) => {
    if (!tag) {
      setPagina(0);
      pesquisarItens();
      return;
    }
    tag = tag.trim();
    const item = await pesquisarItensNaBase([tag]);
    if (item && item.length > 0) {
      setItemSelecionado(item[0]);
      setEpc(tag);
    } else {
      pesquisarItens(tag);
    }
  };

  const limparSelecao = () => {
    setItemSelecionado(undefined);
    setEpc("");
    setPagina(0);
  };

  const sincronizar = async () => {
    await coletaCategorias();
    await coletaSeriais();
    await coletaLocalizacoes();
    setPagina(0);
    await pesquisarItens();
    setItemSelecionado(undefined);
    setEpc("");
    alert.success(t("reimprimir.syncSuccess"));
  };

  // ---- Reimpressão ----
  const gerarListaParaImpressao = async () => {
    if (!impressaoConfig) {
      alert.error(t("reimprimir.configNotFound"));
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(t("reimprimir.informHardwareId"));
      return;
    }
    try {
      if (!itemSelecionado) {
        alert.error(t("reimprimir.noItemSelected"));
        return;
      }
      if (!itemSelecionado.zpl_impressao) {
        alert.error(t("reimprimir.noZpl"));
        return;
      }

      const {
        inf_compl_1,
        inf_compl_2,
        inf_compl_3,
        inf_compl_4,
        inf_compl_5,
        inf_compl_6,
        inf_compl_7,
        inf_compl_8,
        inf_compl_9,
        inf_compl_10,
      } = itemSelecionado;

      const listaParaImpressao: ImpressaoItem = {
        _id: itemSelecionado._id,
        inf_compl_1,
        inf_compl_2,
        inf_compl_3,
        inf_compl_4,
        inf_compl_5,
        inf_compl_6,
        inf_compl_7,
        inf_compl_8,
        inf_compl_9,
        inf_compl_10,
        codigo: itemSelecionado.id_categoria.ean,
        descricao: itemSelecionado.id_categoria.descricao,
        inf_compl_11: "",
        inf_compl_12: "",
        tag: itemSelecionado.tag,
        zpl_impressao: itemSelecionado.zpl_impressao,
        id_categoria: itemSelecionado.id_categoria._id,
        id_conta: itemSelecionado.id_conta,
        id_usuario: itemSelecionado.id_usuario,
        id_localizacao1: itemSelecionado?.id_nivel_loc_1?._id || "",
        id_localizacao2: itemSelecionado?.id_nivel_loc_2?._id || "",
        id_localizacao3: itemSelecionado?.id_nivel_loc_3?._id || "",
        id_localizacao4: itemSelecionado?.id_nivel_loc_4?._id || "",
        valor: itemSelecionado.valor || 0,
        quantidade: itemSelecionado.quantidade
          ? itemSelecionado.quantidade.toString()
          : "",
        dt_validade: brDateToISO(itemSelecionado.dt_validade || ""),
        n_impressao: itemSelecionado?.n_impressao || "0",
        id_externo: "",
        ultima_localizacao: {
          verificar: contaConfig?.transferencia_loc_origem || false,
          primeira_vez: true,
        },
      };

      adicionarItensNaFila(
        [listaParaImpressao],
        "",
        true,
        MODOS_IMPRESSAO.REIMPRESSAO,
      );
      setItemSelecionado(undefined);
      setEpc("");
    } catch (error) {
      console.error("Erro ao gerar lista para impressão:", error);
      alert.error(t("reimprimir.generateError"));
    }
  };

  // ---- Effects ----
  // Marca o instante de montagem para ignorar mensagens WS que sobraram
  // de páginas anteriores (lastMessage persiste no contexto do socket).
  const mountTimeRef = useRef<number>(performance.now());

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.timeStamp < mountTimeRef.current) return;
    if (lastMessage.data?.toLowerCase().includes("lepr+")) {
      const tag = lastMessage.data.split("+")[1].trim();
      coletarItem(tag);
    }
  }, [lastMessage]);

  useEffect(() => {
    pesquisarItens(epc || "*");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina]);

  useEffect(() => {
    return () => {
      setItemSelecionado(undefined);
      setEpc("");
    };
  }, []);

  // ---- Submit do form ----
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPagina(0);
    coletarItem(epc);
  };

  // ---- Helpers ----
  const nomeLoc = (id: string | undefined, nivel: 1 | 2 | 3 | 4): string => {
    if (!id) return "";
    if (nivel === 1) {
      return localizacoes.find((l) => l._id === id)?.descricao || "";
    }
    if (nivel === 2) {
      return (
        localizacoes
          .flatMap((l) => l.children)
          .find((l2) => l2._id === id)?.descricao || ""
      );
    }
    if (nivel === 3) {
      return (
        localizacoes
          .flatMap((l) => l.children)
          .flatMap((l2) => l2.children)
          .find((l3) => l3._id === id)?.descricao || ""
      );
    }
    return (
      localizacoes
        .flatMap((l) => l.children)
        .flatMap((l2) => l2.children)
        .flatMap((l3) => l3.children)
        .find((l4) => l4._id === id)?.descricao || ""
    );
  };

  const camposInfoCompl = [1, 2, 3, 4].map((i) => ({
    idx: i,
    label:
      (contaConfig as any)?.[`info_compl_${i}`] ||
      t("reimprimir.infoCompl", { n: i }),
  }));

  const descricaoItem = itemSelecionado
    ? categorias.find((c) => itemSelecionado.id_categoria?._id === c._id)
        ?.descricao || ""
    : "";

  // ---- Coluna lista ----
  const columnsItensBase: ColumnDef<ImpressaoItemResponse>[] = [
    {
      accessorKey: "tag",
      header: "EPC",
      cell: ({ row }) => (
        <span className="text-xs">{row.original.tag}</span>
      ),
    },
    {
      id: "info_adicional_item",
      header: t("reimprimir.individualInfo"),
      cell: ({ row }) => {
        const { quantidade, valor, dt_validade } = row.original;
        const temValor =
          !!quantidade || (valor && valor > 0) || !!dt_validade;
        if (!temValor)
          return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {quantidade && (
              <Badge variant="secondary" className="font-normal">
                {t("reimprimir.qty")} {quantidade}
              </Badge>
            )}
            {valor > 0 && (
              <Badge variant="secondary" className="font-normal">
                R$ {valor.toFixed(2)}
              </Badge>
            )}
            {dt_validade && (
              <Badge variant="secondary" className="font-normal">
                {t("reimprimir.val")} {formatarData(dt_validade, false)}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "preview",
      header: t("reimprimir.preview"),
      cell: ({ row }) => {
        const item = row.original;
        const zplPadrao = coletaZplPadrao();
        if (zplPadrao) zplPadrao.zpl = item.zpl_impressao;
        if (!zplPadrao || !zplPadrao.zpl) {
          return (
            <Badge variant="secondary" className="text-xs font-normal">
              {t("reimprimir.zplUnavailable")}
            </Badge>
          );
        }
        const url = converterListaZplItemParaUrl(zplPadrao);
        return (
          <HoverCard openDelay={300} closeDelay={50}>
            <HoverCardTrigger asChild>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
              >
                <Eye className="h-3.5 w-3.5" />
                {t("reimprimir.previewBtn")}
              </a>
            </HoverCardTrigger>
            <HoverCardContent className="w-72 p-2">
              <img
                src={url}
                alt={t("reimprimir.previewAlt")}
                className="w-full h-auto bg-white rounded"
              />
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                {t("reimprimir.openInNewTab")}
              </a>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
    {
      id: "select",
      header: () => <div className="text-right">{t("reimprimir.actions")}</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setItemSelecionado(row.original);
              setEpc(row.original.tag);
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            {t("reimprimir.details")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            {t("reimprimir.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("reimprimir.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">{t("reimprimir.sync")}</span>
          </Button>
          <Button
            size="sm"
            disabled={isLoading || !itemSelecionado}
            onClick={gerarListaParaImpressao}
            title={
              !itemSelecionado
                ? t("reimprimir.selectToReprint")
                : t("reimprimir.reprintThis")
            }
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">{t("reimprimir.reprint")}</span>
          </Button>
        </div>
      </div>

      {/* Search Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">
              {t("reimprimir.searchLabel")}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-2"
          >
            <div className="relative flex-1">
              <Tag className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                disabled={isLoading || itemSelecionado !== undefined}
                id="reimprimir-etiqueta-tag"
                type="text"
                value={epc}
                onChange={(e) => setEpc(e.target.value)}
                placeholder={t("reimprimir.typeOrScan")}
                className="pl-8 pr-8"
                autoFocus
              />
              {epc && !itemSelecionado && (
                <button
                  type="button"
                  onClick={() => setEpc("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={t("reimprimir.clearEpc")}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              {itemSelecionado ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={limparSelecao}
                  disabled={isLoading}
                  className="flex-1 sm:flex-initial"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("reimprimir.back")}
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 sm:flex-initial"
                >
                  <Search className="h-4 w-4" />
                  {t("reimprimir.search")}
                </Button>
              )}

              {!itemSelecionado && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={lerEtiqueta}
                        disabled={isLoading}
                        className="flex-1 sm:flex-initial"
                      >
                        <Scan className="h-4 w-4" />
                        {t("reimprimir.readWithModule")}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <div className="flex items-start gap-2">
                        <HelpCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <p>{t("reimprimir.readWithModuleHelp")}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Lista ou detalhe */}
      {!itemSelecionado ? (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Inbox className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  {t("reimprimir.itemsInBase")}
                </CardTitle>
              </div>
              <Badge variant="secondary" className="font-normal">
                {t("reimprimir.ofTotal", {
                  count: itens.length,
                  total: totalPaginas * 10,
                })}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {itens.length === 0 && !isLoading ? (
              <div className="py-12 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <Inbox className="h-8 w-8 opacity-40" />
                <p className="text-sm">{t("reimprimir.noItemFound")}</p>
                {epc && (
                  <p className="text-xs">
                    {t("reimprimir.noResultsFor")}{" "}
                    <span className="">{epc}</span>
                  </p>
                )}
              </div>
            ) : (
              <DataTableNoFilter
                columns={columnsItensBase}
                data={itens}
                onNextPage={() => {
                  if (pagina < totalPaginas - 1) setPagina(pagina + 1);
                }}
                onPreviousPage={() => {
                  if (pagina > 0) setPagina(pagina - 1);
                }}
                onPageChange={(p) => setPagina(p)}
                page={pagina}
                totalPages={totalPaginas}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Hero da seleção + ações */}
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
                  <Tag className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t("reimprimir.selectedLabel")}
                  </p>
                  <p className="text-sm font-medium truncate">
                    {itemSelecionado.tag}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={limparSelecao}
                  disabled={isLoading}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("reimprimir.backToList")}
                </Button>
                <Button
                  size="sm"
                  onClick={gerarListaParaImpressao}
                  disabled={isLoading}
                >
                  <Printer className="h-4 w-4" />
                  {t("reimprimir.reprint")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Localizações */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  {t("reimprimir.locations")}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((nivel) => {
                  const id =
                    nivel === 1
                      ? itemSelecionado?.id_nivel_loc_1?._id
                      : nivel === 2
                        ? itemSelecionado?.id_nivel_loc_2?._id
                        : nivel === 3
                          ? itemSelecionado?.id_nivel_loc_3?._id
                          : itemSelecionado?.id_nivel_loc_4?._id;
                  const valor = nomeLoc(id, nivel as 1 | 2 | 3 | 4);
                  return (
                    <div key={nivel} className="grid gap-2">
                      <Label
                        htmlFor={`reimprimir-etiqueta-loc-${nivel}`}
                        className="text-xs text-muted-foreground"
                      >
                        {t("reimprimir.level", { n: nivel })}
                      </Label>
                      <Input
                        disabled
                        id={`reimprimir-etiqueta-loc-${nivel}`}
                        type="text"
                        value={valor || "—"}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Item */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  {t("reimprimir.itemData")}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2">
                <Label
                  htmlFor="reimprimir-etiqueta-descricao"
                  className="text-xs text-muted-foreground"
                >
                  {t("reimprimir.description")}
                </Label>
                <Input
                  disabled
                  id="reimprimir-etiqueta-descricao"
                  type="text"
                  value={descricaoItem || "—"}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {camposInfoCompl.map((c) => (
                  <div key={c.idx} className="grid gap-2">
                    <Label
                      htmlFor={`reimprimir-etiqueta-info-${c.idx}`}
                      className="text-xs text-muted-foreground"
                    >
                      {c.label}
                    </Label>
                    <Input
                      disabled
                      id={`reimprimir-etiqueta-info-${c.idx}`}
                      type="text"
                      value={
                        ((itemSelecionado as any)?.[
                          `inf_compl_${c.idx}`
                        ] as string) || "—"
                      }
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Etiqueta */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  {t("reimprimir.labelTitle")}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-center">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    EPC
                  </p>
                  <p className="text-sm truncate">
                    {itemSelecionado.tag}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t("reimprimir.individualInfo")}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {itemSelecionado.quantidade && (
                      <Badge variant="secondary" className="font-normal">
                        {t("reimprimir.qty")} {itemSelecionado.quantidade}
                      </Badge>
                    )}
                    {itemSelecionado.valor > 0 && (
                      <Badge variant="secondary" className="font-normal">
                        R$ {itemSelecionado.valor.toFixed(2)}
                      </Badge>
                    )}
                    {itemSelecionado.dt_validade && (
                      <Badge variant="secondary" className="font-normal">
                        {t("reimprimir.val")}{" "}
                        {formatarData(itemSelecionado.dt_validade, false)}
                      </Badge>
                    )}
                    {!itemSelecionado.quantidade &&
                      !itemSelecionado.valor &&
                      !itemSelecionado.dt_validade && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                  </div>
                </div>
                <div className="sm:justify-self-end">
                  {(() => {
                    const zplPadrao = coletaZplPadrao();
                    if (zplPadrao)
                      zplPadrao.zpl = itemSelecionado.zpl_impressao;
                    if (!zplPadrao || !zplPadrao.zpl) {
                      return (
                        <Badge
                          variant="secondary"
                          className="text-xs font-normal"
                        >
                          {t("reimprimir.zplUnavailable")}
                        </Badge>
                      );
                    }
                    const url = converterListaZplItemParaUrl(zplPadrao);
                    return (
                      <HoverCard openDelay={300} closeDelay={50}>
                        <HoverCardTrigger asChild>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            {t("reimprimir.previewBtn")}
                          </a>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-72 p-2">
                          <img
                            src={url}
                            alt={t("reimprimir.previewAlt")}
                            className="w-full h-auto bg-white rounded"
                          />
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {t("reimprimir.openInNewTab")}
                          </a>
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default ReimprimirEtiqueta;
